import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Printer, ArrowRightLeft, Pencil, Shuffle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, fmtQty, fmtDT, TXN_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, Field, Combobox, tableWrap, th, td, num } from "@/components/common";

function Transfer({ drum, onClose, onDone }) {
  const { lookups } = useAuth();
  const [wh, setWh] = useState(drum.warehouse);
  const [rack, setRack] = useState(drum.rack);
  const go = async () => {
    try { await api.post(`/drums/${drum.id}/transfer`, { warehouse: wh, rack }); toast.success("Drum moved"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="drum-transfer-dialog">
        <DialogHeader><DialogTitle>Internal transfer · {drum.drum_code}</DialogTitle></DialogHeader>
        <Field label="Warehouse"><Combobox options={(lookups?.warehouses || []).map((w) => ({ value: w, label: w }))} value={wh} onChange={setWh} testid="drum-transfer-warehouse" /></Field>
        <Field label="Rack"><Input value={rack} onChange={(e) => setRack(e.target.value)} data-testid="drum-transfer-rack-input" /></Field>
        <DialogFooter><Button onClick={go} data-testid="drum-transfer-save-btn">Move drum</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDrum({ drum, onClose, onDone }) {
  const [f, setF] = useState({ quantity: String(drum.current_balance), mfr_drum_no: drum.mfr_drum_no || "", warehouse: drum.warehouse || "", rack: drum.rack || "", cost_rate: String(drum.cost_rate ?? ""), reason: "" });
  const [busy, setBusy] = useState(false);
  const s = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const canQty = ["drum", "offcut", "lot"].includes(drum.kind);
  const go = async () => {
    setBusy(true);
    try {
      const body = { mfr_drum_no: f.mfr_drum_no, warehouse: f.warehouse, rack: f.rack, reason: f.reason };
      if (canQty && f.quantity !== "") body.quantity = +f.quantity;
      if (f.cost_rate !== "") body.cost_rate = +f.cost_rate;
      const { data } = await api.post(`/stock-edit/drum/${drum.id}`, body);
      toast.success(data.adj_no ? `Saved · quantity change recorded as ${data.adj_no}` : "Saved");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const diff = canQty && f.quantity !== "" ? +f.quantity - drum.current_balance : 0;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg" data-testid="drum-edit-dialog">
        <DialogHeader><DialogTitle>Edit {drum.drum_code}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {canQty && <Field label={`Quantity now (${drum.uom})`} className="col-span-2"><Input type="number" min="0" step="0.001" value={f.quantity} onChange={s("quantity")} data-testid="drum-edit-quantity" />
            {diff !== 0 && <div className={`text-xs mt-1 ${diff > 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="drum-edit-diff">{diff > 0 ? "+" : ""}{fmtQty(diff, drum.uom)} · saved as an adjustment</div>}</Field>}
          <Field label="Mfr drum number"><Input value={f.mfr_drum_no} onChange={s("mfr_drum_no")} data-testid="drum-edit-mfr" /></Field>
          <Field label="Purchase rate ₹"><Input type="number" min="0" step="0.01" value={f.cost_rate} onChange={s("cost_rate")} data-testid="drum-edit-rate" /></Field>
          <Field label="Warehouse"><Input value={f.warehouse} onChange={s("warehouse")} data-testid="drum-edit-warehouse" /></Field>
          <Field label="Rack"><Input value={f.rack} onChange={s("rack")} data-testid="drum-edit-rack" /></Field>
          <Field label="Reason for change" required className="col-span-2"><Input value={f.reason} onChange={s("reason")} placeholder="e.g. wrong metres entered in opening stock" data-testid="drum-edit-reason" /></Field>
        </div>
        <DialogFooter><Button disabled={busy || f.reason.trim().length < 3} onClick={go} className="bg-red-600 hover:bg-red-700 text-white" data-testid="drum-edit-save-btn">{busy ? "Saving…" : "Save changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoveDrum({ drum, onClose, onDone }) {
  const [prods, setProds] = useState([]);
  const [to, setTo] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/products", { params: { status: "active" } }).then((r) => setProds((r.data.items || r.data).filter((p) => p.id !== drum.product_id && p.uom === drum.uom))); }, [drum]);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/stock-edit/move", { drum_ids: [drum.id], to_product_id: to, reason }); toast.success(`Moved · ${data.ref_no}`); onDone(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-lg" data-testid="drum-move-dialog">
        <DialogHeader><DialogTitle>Move {drum.drum_code} to the right product</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Now: {drum.brand} · {drum.cable_size} · {drum.product_name} · {fmtQty(drum.current_balance, drum.uom)}</p>
        <Field label="Move to product" required className="min-w-0 [&_button]:max-w-full [&_button]:truncate"><Combobox options={prods.map((p) => ({ value: p.id, label: `${p.brand} · ${p.cable_size} · ${p.name} · ${p.sku}` }))} value={to} onChange={setTo} testid="drum-move-product" placeholder="Search brand or size" /></Field>
        <Field label="Reason" required><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. entered under wrong size" data-testid="drum-move-reason" /></Field>
        <DialogFooter><Button disabled={busy || !to || reason.trim().length < 3} onClick={go} className="bg-red-600 hover:bg-red-700 text-white" data-testid="drum-move-save-btn">{busy ? "Moving…" : "Move drum"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DrumDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [tx, setTx] = useState(false);
  const [ed, setEd] = useState(null);
  const load = () => api.get(`/drums/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return null;
  const qrUrl = `${window.location.origin}/drums/${d.id}`;
  const info = [["Brand", d.brand], ["Cable Size", d.cable_size], ["Cable Type", d.cable_type], ["SKU", d.sku], ["Mfr Drum No", d.mfr_drum_no], ["Original Length", fmtQty(d.original_length, d.uom)],
    ["Current Uncut Balance", fmtQty(d.current_balance, d.uom)], ["Reserved", fmtQty(d.reserved, d.uom)], ["Warehouse", d.warehouse], ["Rack", d.rack], ["Supplier", d.supplier_name], ["Receipt Date", d.receipt_date], ["Source", d.source_ref]];
  return (
    <div className="space-y-6">
      <div className="no-print">
        <PageHeader title={<span className="font-mono">{d.drum_code}</span>} subtitle={d.product_name}>
          <StatusBadge status={d.status} testid="drum-detail-status" />
          {can("inward:post") && <Button variant="outline" onClick={() => setTx(true)} data-testid="drum-transfer-btn"><ArrowRightLeft className="h-4 w-4 mr-1" />Transfer</Button>}
          {can("stock:edit") && d.status !== "reversed" && <Button variant="outline" onClick={() => setEd("edit")} data-testid="drum-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>}
          {can("stock:edit") && ["full", "partial"].includes(d.status) && ["drum", "offcut", "lot"].includes(d.kind) && <Button variant="outline" onClick={() => setEd("move")} data-testid="drum-move-btn"><Shuffle className="h-4 w-4 mr-1" />Move to product</Button>}
          <Button onClick={() => window.print()} data-testid="drum-print-qr-btn"><Printer className="h-4 w-4 mr-1" />Print QR label</Button>
        </PageHeader>
      </div>
      <div className="grid lg:grid-cols-[1fr_280px] gap-4">
        <div className="rounded-lg border bg-card p-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm no-print" data-testid="drum-info">
          {info.map(([k, v]) => <div key={k}><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{k}</div><div className="font-medium font-mono break-words" data-testid={`drum-info-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v || "—"}</div></div>)}
        </div>
        <div className="print-area rounded-lg border bg-white text-black p-4 flex flex-col items-center gap-2" data-testid="drum-qr-label">
          <div className="font-bold tracking-tight">ROXY CABLES</div>
          <QRCodeSVG value={qrUrl} size={160} />
          <div className="font-mono font-bold text-lg">{d.drum_code}</div>
          <div className="text-xs text-center">{d.brand} · {d.cable_size}</div>
          <div className="text-xs font-mono">Mfr: {d.mfr_drum_no || "—"} · {fmtQty(d.original_length, d.uom)}</div>
          <div className="text-[10px] text-gray-500">Scan requires staff login</div>
        </div>
      </div>
      <div className="no-print">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">Movement history (immutable ledger)</div>
        <div className={tableWrap}>
          <table className="w-full" data-testid="drum-movements-table">
            <thead><tr>{["Seq", "Time", "Type", "Reference", "Qty", "Balance After", "By", "Remarks"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody>{d.movements.map((m) => (
              <tr key={m.id}><td className={`${td} font-mono`}>{m.seq}</td><td className={td}>{fmtDT(m.at)}</td><td className={td}>{TXN_LABELS[m.txn_type]}</td><td className={`${td} font-mono`}>{m.ref_no}</td>
                <td className={`${td} ${num} ${m.qty < 0 ? "text-red-600" : ""}`}>{fmtQty(m.qty, m.uom)}</td><td className={`${td} ${num}`}>{fmtQty(m.drum_balance_after, m.uom)}</td><td className={td}>{m.by.name}</td><td className={td}>{m.remarks}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
      {tx && <Transfer drum={d} onClose={() => setTx(false)} onDone={() => { setTx(false); load(); }} />}
      {ed === "edit" && <EditDrum drum={d} onClose={() => setEd(null)} onDone={() => { setEd(null); load(); }} />}
      {ed === "move" && <MoveDrum drum={d} onClose={() => setEd(null)} onDone={() => { setEd(null); load(); }} />}
    </div>
  );
}
